import React from "react";
import { Camera, X, CheckCircle2, Loader2, Trash2, Image as ImageIcon } from "lucide-react";
import { driverApi } from "../lib/api";
import { toast } from "sonner";

const SCENE_PRESETS = [
  "Afleveret til kunden",
  "Pakken ved døren (fuldmagt)",
  "Cykelskur / garage",
  "Ingen modtog — billede af adresse",
  "Andet",
];

// Compress + resize image client-side: max 1280px, JPEG ~0.7 quality, ~150-300KB
const compressImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const maxDim = 1280;
      let w = img.width, h = img.height;
      if (w > h && w > maxDim) { h = Math.round(h * maxDim / w); w = maxDim; }
      else if (h > maxDim) { w = Math.round(w * maxDim / h); h = maxDim; }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.72));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function DeliverProofModal({ booking, onClose, onDelivered }) {
  const [photos, setPhotos] = React.useState([]); // [{label, data}]
  const [pendingScene, setPendingScene] = React.useState(SCENE_PRESETS[0]);
  const [customLabel, setCustomLabel] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const fileRef = React.useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= 4) { toast.error("Maks 4 billeder"); return; }
    setUploadingFile(true);
    try {
      const data = await compressImage(file);
      const label = pendingScene === "Andet" ? (customLabel.trim() || "Bevis") : pendingScene;
      setPhotos((p) => [...p, { label, data }]);
      setCustomLabel("");
    } catch {
      toast.error("Kunne ikke læse billedet");
    } finally { setUploadingFile(false); }
  };

  const remove = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async () => {
    if (photos.length < 1) { toast.error("Tag mindst 1 billede"); return; }
    setBusy(true);
    try {
      const { data } = await driverApi().post(`/driver/bookings/${booking.id}/deliver`, {
        photos, note: note || undefined,
      });
      toast.success(`Leveret · ${data.photo_count} billede${data.photo_count > 1 ? "r" : ""} gemt`);
      onDelivered?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme levering");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-5" data-testid="deliver-modal">
      <div className="bg-white w-full sm:max-w-md max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-brand-dark text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="kicker text-brand-red">Bekræft levering</div>
            <div className="font-display font-bold text-lg mt-1">{booking.booking_number}</div>
          </div>
          <button onClick={onClose} data-testid="close-modal" className="w-9 h-9 flex items-center justify-center hover:bg-white/10"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Booking summary */}
          <div className="bg-brand-bg2 p-3 text-sm">
            <div className="font-semibold text-brand-dark">{booking.customer_name}</div>
            <div className="text-xs text-brand-text2">{booking.delivery_address} · {booking.delivery_zip} {booking.delivery_city}</div>
            {booking.delivery_instruction && (
              <div className="mt-2 pt-2 border-t border-brand text-xs">
                <span className="font-bold text-amber-700">Fuldmagt:</span> {booking.delivery_instruction}
              </div>
            )}
          </div>

          {/* Scene picker */}
          <div>
            <label className="label-flat">Scene for næste foto</label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {SCENE_PRESETS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setPendingScene(s)}
                  data-testid={`scene-${s}`}
                  className={`py-2 px-3 text-xs font-semibold border ${pendingScene === s ? "border-brand-dark bg-brand-dark text-white" : "border-brand text-brand-dark"}`}
                >
                  {s}
                </button>
              ))}
            </div>
            {pendingScene === "Andet" && (
              <input
                className="input-flat mt-2"
                placeholder="Beskriv scenen..."
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                data-testid="custom-label"
              />
            )}
          </div>

          {/* Take photo button */}
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPick}
              className="hidden"
              data-testid="file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingFile || photos.length >= 4}
              data-testid="take-photo"
              className="w-full bg-brand-red text-white font-display font-bold py-4 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {uploadingFile ? <Loader2 className="animate-spin" size={18} /> : <Camera size={18} />}
              Tag billede ({photos.length}/4)
            </button>
            <div className="text-xs text-brand-text2 mt-2 text-center">Mindst 1 billede påkrævet · maks 4</div>
          </div>

          {/* Photo previews */}
          {photos.length > 0 && (
            <div className="grid grid-cols-2 gap-2" data-testid="photo-previews">
              {photos.map((p, i) => (
                <div key={i} className="relative border border-brand">
                  <img src={p.data} alt="" className="w-full aspect-[4/3] object-cover" />
                  <div className="absolute top-1 left-1 right-9 bg-brand-dark/90 text-white text-[10px] px-2 py-1 truncate">{p.label}</div>
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    data-testid={`remove-photo-${i}`}
                    className="absolute top-1 right-1 w-7 h-7 bg-brand-red text-white flex items-center justify-center"
                    aria-label="Fjern"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Optional note */}
          <div>
            <label className="label-flat">Note (valgfri)</label>
            <textarea rows={2} className="input-flat" value={note} onChange={(e) => setNote(e.target.value)} data-testid="proof-note" placeholder="F.eks. Modtaget af nabo · Pakken efterladt på terrasse..." />
          </div>

          {/* Submit */}
          <button
            type="button"
            onClick={submit}
            disabled={busy || photos.length < 1}
            data-testid="confirm-delivery"
            className="w-full bg-[#00B67A] text-white font-display font-bold py-4 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {busy ? <><Loader2 className="animate-spin" size={18} /> Gemmer...</> : <><CheckCircle2 size={18} /> Bekræft levering</>}
          </button>
        </div>
      </div>
    </div>
  );
}
